/**
 * StarRating Component
 *
 * Interactive 1-5 star rating control for critics to rate photos.
 * Supports keyboard navigation and accessibility.
 *
 * Feature: 649-critique-feedback-options (T029)
 */

import { useState, useCallback, KeyboardEvent } from 'react';
import { Star } from '@/components/atoms/Star/Star';
import { cn } from '@/lib/utils';
import logger from '@/utils/logger';

export interface StarRatingProps {
  /** Current rating value (1-5 or null for no rating) */
  value: number | null;
  /** Callback when rating changes */
  onChange: (rating: number) => void;
  /** Whether the control is disabled */
  disabled?: boolean;
  /** Whether a save operation is in progress */
  loading?: boolean;
  /** Size of each star in pixels */
  size?: number;
  /** Additional CSS classes for the container */
  className?: string;
  /** Accessible label for the rating control */
  'aria-label'?: string;
}

/**
 * Interactive star rating control
 *
 * @example
 * ```tsx
 * <StarRating
 *   value={3}
 *   onChange={handleRatingChange}
 *   disabled={isPhotographer}
 * />
 * ```
 */
export function StarRating({
  value,
  onChange,
  disabled = false,
  loading = false,
  size = 28,
  className,
  'aria-label': ariaLabel = 'Rate this photo',
}: StarRatingProps) {
  // Hover state for preview
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  // Determine which value to display (hover preview or actual value)
  const displayValue = hoverValue ?? value ?? 0;

  const handleStarClick = useCallback(
    (rating: number) => {
      if (disabled || loading) return;

      logger.debug('StarRating', 'Star clicked', { rating, currentValue: value });
      onChange(rating);
    },
    [disabled, loading, onChange, value]
  );

  const handleStarHover = useCallback(
    (rating: number | null) => {
      if (disabled || loading) return;
      setHoverValue(rating);
    },
    [disabled, loading]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (disabled || loading) return;

      const currentValue = value ?? 0;
      let newValue: number | null = null;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          newValue = Math.min(currentValue + 1, 5);
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
          newValue = Math.max(currentValue - 1, 1);
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
          newValue = parseInt(event.key, 10);
          break;
        case 'Home':
          newValue = 1;
          break;
        case 'End':
          newValue = 5;
          break;
        default:
          return;
      }

      if (newValue !== null) {
        event.preventDefault();
        onChange(newValue);
      }
    },
    [disabled, loading, onChange, value]
  );

  const isInteractive = !disabled && !loading;

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1',
        disabled && 'opacity-50',
        loading && 'animate-pulse',
        className
      )}
      role="slider"
      aria-label={ariaLabel}
      aria-valuemin={1}
      aria-valuemax={5}
      aria-valuenow={value ?? undefined}
      aria-valuetext={value ? `${value} out of 5 stars` : 'No rating'}
      aria-disabled={disabled}
      tabIndex={isInteractive ? 0 : -1}
      onKeyDown={handleKeyDown}
      onMouseLeave={() => handleStarHover(null)}
    >
      {[1, 2, 3, 4, 5].map((rating) => (
        <button
          key={rating}
          type="button"
          disabled={!isInteractive}
          onClick={() => handleStarClick(rating)}
          onMouseEnter={() => handleStarHover(rating)}
          className={cn(
            'p-0.5 rounded transition-transform',
            isInteractive && 'hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
            isInteractive && 'cursor-pointer',
            !isInteractive && 'cursor-default'
          )}
          aria-label={`Rate ${rating} star${rating !== 1 ? 's' : ''}`}
          tabIndex={-1} // Main container handles keyboard navigation
        >
          <Star
            fill={rating <= displayValue ? 'full' : 'empty'}
            size={size}
            fillColor={rating <= displayValue ? 'rgb(var(--color-primary))' : 'rgb(var(--color-border))'}
            emptyColor="transparent"
          />
        </button>
      ))}

      {/* Screen reader announcement */}
      {value && (
        <span className="sr-only">
          Current rating: {value} out of 5 stars
        </span>
      )}
    </div>
  );
}

StarRating.displayName = 'StarRating';
